<template>
  <div class="section-content">
    <div class="section-header">
      <div class="section-title">CONSUMPTION :</div>
    </div>

    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>Loading consumption data...</span>
    </div>
    <div v-else-if="error" class="error-state">
      <div class="error-message">{{ error }}</div>
      <button class="retry-button" @click="$emit('retry')">Retry</button>
    </div>
    <div v-else-if="readings && readings.length > 1" class="chart-container">
      <div class="chart-header">
      </div>

      <div class="chart-body" v-if="consumptionData.length > 0">
        <Line :data="chartData" :options="chartOptions" />
      </div>

      <div v-else class="no-consumption-data">
        <p>Unable to calculate consumption from available readings.</p>
      </div>
    </div>
    <div v-else class="no-data-message">
      <div class="info-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <p>No consumption data available.</p>
      <p class="secondary-text">Consumption chart will appear once more reading data is collected.</p>
    </div>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
} from 'chart.js'
import { Line } from 'vue-chartjs'
import { amrRawToM3 } from '@/services/loraWbusHelper'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Filler
)

export default {
  name: 'DeviceDetailsReadingsChart',
  components: {
    Line
  },
  props: {
    readings: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isLoading: false,
      error: ''
    };
  },
  computed: {
    // Calculate consumption data from consecutive readings
    consumptionData() {
      if (!this.readings || this.readings.length < 2) return [];

      // Sort readings by date (oldest first) for proper consumption calculation
      const sortedReadings = [...this.readings].sort((a, b) =>
          new Date(a.date_current_reading) - new Date(b.date_current_reading)
      );

      const consumption = [];
      for (let i = 1; i < sortedReadings.length; i++) {
        const currentReading = sortedReadings[i];
        const previousReading = sortedReadings[i - 1];

        const current = this.readingM3Value(currentReading);
        const previous = this.readingM3Value(previousReading);
        const diff = current - previous;

        consumption.push({
          value: Math.max(0, diff), // Ensure non-negative consumption
          timestamp: currentReading.date_current_reading,
          originalIndex: i
        });
      }
      return consumption;
    },

    // Compute total consumption by summing all consumption values
    totalConsumption() {
      if (this.consumptionData.length === 0) return 0;
      return this.consumptionData.reduce((sum, item) => sum + item.value, 0);
    },

    // Chart.js data configuration
    chartData() {
      if (this.consumptionData.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      const labels = this.consumptionData.map(item => this.formatDateLabel(item.timestamp));
      const data = this.consumptionData.map(item => item.value);

      return {
        labels,
        datasets: [
          {
            label: 'Consumption (m³)',
            data,
            borderColor: '#4fc3f7', // Your blue color
            backgroundColor: 'rgba(79, 195, 247, 0.1)', // Semi-transparent fill
            tension: 0.3, // Smooth curves
            pointRadius: 0, // Hide dots by default
            pointHoverRadius: 6, // Show dots on hover
            pointHoverBackgroundColor: '#4fc3f7',
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 2,
            fill: true, // Fill area under the curve
            borderWidth: 2
          }
        ]
      };
    },

    // Chart.js options configuration
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false // Hide default legend
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: 'white',
            bodyColor: 'white',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: (context) => {
                return this.formatTooltipDate(context[0].label);
              },
              label: (context) => {
                return `${context.parsed.y.toFixed(3)} m³`;
              }
            }
          }
        },
        interaction: {
          intersect: false, // Show tooltip when hovering near the line
          mode: 'index'
        },
        scales: {
          x: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: {
                size: 10
              },
              maxTicksLimit: 5 // Limit number of x-axis labels
            },
            grid: {
              display: false // Hide vertical grid lines
            },
            border: {
              display: false // Hide x-axis border
            }
          },
          y: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: {
                size: 10
              },
              callback: function(value) {
                return value.toFixed(1) + ' m³';
              }
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)',
              drawTicks: false // Hide tick marks
            },
            border: {
              display: false,
              dash: [5, 10] // Dashed grid lines
            }
          }
        }
      };
    }
  },
  methods: {
    readingM3Value(reading) {
      const additionalData = reading?.additional_data || {};
      const rawValue = additionalData.Volume?.Raw ?? reading?.current_reading;
      const converted = amrRawToM3(rawValue, additionalData);

      if (converted !== null) return converted;

      return Number(reading?.current_reading) || 0;
    },

    formatDateLabel(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${day}/${month}`;
    },

    formatTooltipDate(dateString) {
      // If it's already in DD/MM format, convert back to full date
      if (dateString.includes('/') && dateString.length === 5) {
        // Find the original timestamp for this label
        const labelIndex = this.chartData.labels.indexOf(dateString);
        if (labelIndex >= 0 && this.consumptionData[labelIndex]) {
          const date = new Date(this.consumptionData[labelIndex].timestamp);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        }
      }
      return dateString;
    }
  }
};
</script>

<style scoped>
.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

/* Chart container styling */
.chart-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.chart-metrics {
  display: flex;
  flex-direction: column;
}

.chart-metric-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
}

.chart-metric-value {
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.chart-period {
  background-color: rgba(255, 255, 255, 0.1);
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 500;
  color: white;
}

.chart-body {
  position: relative;
  flex-grow: 1;
  min-height: 200px;
  max-height: 330px;
  height: 200px; /* Fixed height for Chart.js */
}

.no-consumption-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.no-consumption-data p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* No data message */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
}

.no-data-message p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
  color: white;
}

.secondary-text {
  font-size: 11px !important;
  opacity: 0.7;
}
</style>
